import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AdminLayout } from '../../components/AdminLayout';
import { Card } from '../../styles/GlobalStyles';
import { clubService } from '../../services/clubService';
import { Club, ClubSubscription, SubscriptionTier } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';

const SettingsCard = styled(Card)`
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-lg);
`;

const SectionTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md) 0;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-xs);
  }
`;

const SettingLabel = styled.div`
  font-weight: 500;
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const SettingValue = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  text-align: right;

  @media (max-width: 767px) {
    text-align: left;
  }
`;

const EditButton = styled.button`
  padding: var(--spacing-sm) var(--spacing-lg);
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  font-size: 0.875rem;
  min-height: 44px;
  margin-top: var(--spacing-md);

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-md);
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: 1rem;
  min-height: 44px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }

  &:disabled {
    background-color: var(--disabled-bg, #f5f5f5);
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
  margin-top: var(--spacing-lg);

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: var(--spacing-sm) var(--spacing-lg);
  background-color: ${props => props.$variant === 'primary' ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.$variant === 'primary' ? 'white' : 'var(--text-primary)'};
  border: ${props => props.$variant === 'primary' ? 'none' : '1px solid var(--border-color)'};
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  font-size: 0.875rem;
  min-height: 44px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const ErrorMessage = styled.div`
  padding: var(--spacing-lg);
  background-color: var(--error-bg, #fee);
  color: var(--error-color, #c00);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-lg);
`;

const SuccessMessage = styled.div`
  padding: var(--spacing-lg);
  background-color: #00800020;
  color: #008000;
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-lg);
`;

const SubscriptionBadge = styled.span<{ $active: boolean }>`
  display: inline-block;
  padding: 4px 12px;
  background-color: ${props => props.$active ? '#00800020' : '#80808020'};
  color: ${props => props.$active ? '#008000' : '#808080'};
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const AdminSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [club, setClub] = useState<Club | null>(null);
  const [subscription, setSubscription] = useState<ClubSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    affiliatedFA: '',
    address: ''
  });

  useEffect(() => {
    fetchClubData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.profile?.clubId]);

  const fetchClubData = async () => {
    if (!user?.profile?.clubId) {
      setError('No club associated with your account');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const [clubData, subscriptionData] = await Promise.all([
        clubService.getClub(user.profile.clubId),
        clubService.getSubscription(user.profile.clubId)
      ]);
      
      setClub(clubData);
      setSubscription(subscriptionData);
      setEditForm({
        name: clubData.name,
        affiliatedFA: clubData.affiliatedFA,
        address: clubData.address
      });
    } catch (err: any) {
      console.error('Failed to fetch club data:', err);
      setError(err.response?.data?.error?.message || 'Failed to load club settings');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setError(null);
    setSuccess(null);
    // Reset form to current club data
    if (club) {
      setEditForm({
        name: club.name,
        affiliatedFA: club.affiliatedFA,
        address: club.address
      });
    }
  };

  const handleSaveChanges = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.profile?.clubId) return;

    // Validate club name
    if (!editForm.name.trim()) {
      setError('Club name cannot be empty');
      return;
    }

    if (editForm.name.length > 100) {
      setError('Club name cannot exceed 100 characters');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const updatedClub = await clubService.updateClub(user.profile.clubId, {
        name: editForm.name.trim(),
        affiliatedFA: editForm.affiliatedFA.trim(),
        address: editForm.address.trim()
      });

      setClub(updatedClub);
      setIsEditing(false);
      setSuccess('Club settings updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Failed to update club:', err);
      setError(err.response?.data?.error?.message || 'Failed to update club settings');
    } finally {
      setSaving(false);
    }
  };

  const formatSubscriptionTier = (tier: SubscriptionTier | null): string => {
    if (!tier) return 'No Active Subscription';
    return tier.charAt(0) + tier.slice(1).toLowerCase();
  };

  if (loading) {
    return (
      <AdminLayout pageTitle="Settings">
        <LoadingSpinner />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Settings">
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <SettingsCard>
        <SectionTitle>Club Information</SectionTitle>
        
        {!isEditing ? (
          <>
            <SettingRow>
              <SettingLabel>Club Name</SettingLabel>
              <SettingValue>{club?.name || 'Not Set'}</SettingValue>
            </SettingRow>
            <SettingRow>
              <SettingLabel>Affiliated FA</SettingLabel>
              <SettingValue>{club?.affiliatedFA || 'Not Set'}</SettingValue>
            </SettingRow>
            <SettingRow>
              <SettingLabel>Club Address</SettingLabel>
              <SettingValue>{club?.address || 'Not Set'}</SettingValue>
            </SettingRow>
            <EditButton onClick={handleEditClick}>
              Edit Club Details
            </EditButton>
          </>
        ) : (
          <form onSubmit={handleSaveChanges}>
            <FormGroup>
              <Label htmlFor="clubName">Club Name *</Label>
              <Input
                id="clubName"
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                required
                maxLength={100}
                disabled={saving}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="affiliatedFA">Affiliated FA *</Label>
              <Input
                id="affiliatedFA"
                type="text"
                value={editForm.affiliatedFA}
                onChange={(e) => setEditForm({ ...editForm, affiliatedFA: e.target.value })}
                required
                disabled={saving}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="address">Club Address *</Label>
              <TextArea
                id="address"
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                required
                disabled={saving}
              />
            </FormGroup>

            <ButtonGroup>
              <Button 
                type="button"
                $variant="secondary" 
                onClick={handleCancelEdit}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button 
                type="submit"
                $variant="primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </ButtonGroup>
          </form>
        )}
      </SettingsCard>

      <SettingsCard>
        <SectionTitle>Subscription</SectionTitle>
        <SettingRow>
          <SettingLabel>Current Plan</SettingLabel>
          <SettingValue>
            {formatSubscriptionTier(subscription?.tier || null)}
            {' '}
            {subscription && (
              <SubscriptionBadge $active={subscription.status === 'ACTIVE'}>
                {subscription.status}
              </SubscriptionBadge>
            )}
          </SettingValue>
        </SettingRow>
        <SettingRow>
          <SettingLabel>Team Limit</SettingLabel>
          <SettingValue>{subscription?.teamLimit || 0} teams</SettingValue>
        </SettingRow>
        {subscription && subscription.currentPeriodEnd && (
          <SettingRow>
            <SettingLabel>Current Period Ends</SettingLabel>
            <SettingValue>
              {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
            </SettingValue>
          </SettingRow>
        )}
        {subscription?.discountCodeApplied && (
          <SettingRow>
            <SettingLabel>Discount Code Applied</SettingLabel>
            <SettingValue>{subscription.discountCodeApplied}</SettingValue>
          </SettingRow>
        )}
      </SettingsCard>
    </AdminLayout>
  );
};
